import { TokenType, } from "../../../model/enums/SDKTokenType";
import { BorderToken } from "../../../model/tokens/SDKBorderToken";
import { ColorToken } from "../../../model/tokens/SDKColorToken";
import { resolveDimension, } from "../../../model/tokens/SDKDimensionToken";
import { GradientToken } from "../../../model/tokens/SDKGradientToken";
import { ShadowToken } from "../../../model/tokens/SDKShadowToken";
import { resolveString, } from "../../../model/tokens/SDKStringToken";
import { TextCaseToken } from "../../../model/tokens/SDKTextCaseToken";
import { TextDecorationToken } from "../../../model/tokens/SDKTextDecorationToken";
import { TypographyToken } from "../../../model/tokens/SDKTypographyToken";
import { VisibilityToken } from "../../../model/tokens/SDKVisibilityToken";
import { UnreachableCaseError } from "../../../utils/UnreachableCaseError";
import { DTTokenMerger } from "./SDKDTTokenMerger";
import { DTTokenReferenceResolver } from "./SDKDTTokenReferenceResolver";
// --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
// MARK: - Tool implementation
/** Utility to DS Tokens plugin JSON to Supernova entities */
export class DTJSONConverter {
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Constructor
    constructor(versionId, mapping) {
        this.versionId = versionId;
        this.mapping = mapping;
        this.referenceResolver = new DTTokenReferenceResolver();
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Token Conversion
    convertNodesToTokens(nodes, brand) {
        // Compute dimensions first. Dimensions can be used to do all types of calculation, so they must be available at the beginning for all other types of tokens
        this.convertNodesToTokensForSupportedNodeTypes([
            "sizing",
            "borderWidth",
            "spacing",
            "opacity",
            "fontSizes",
            "paragraphSpacing",
            "lineHeights",
            "letterSpacing",
            "other",
            "dimension",
            "number",
            "duration",
        ], nodes, brand);
        // String tokens
        this.convertNodesToTokensForSupportedNodeTypes(["fontFamilies", "fontWeights", "text"], nodes, brand);
        // Option tokens
        this.convertNodesToTokensForSupportedNodeTypes(["textCase", "textDecoration"], nodes, brand);
        // Other tokens
        // this.convertNodesToTokensForSupportedNodeTypes(['other'], nodes, brand)
        // Color & Gradient tokens, they could reference each other
        this.convertNodesToTokensForSupportedNodeTypes(["color", "gradient"], nodes, brand);
        // Radii tokens
        this.convertNodesToTokensForSupportedNodeTypes(["borderRadius"], nodes, brand);
        // Shadow tokens
        this.convertNodesToTokensForSupportedNodeTypes(["boxShadow"], nodes, brand);
        // Typography tokens
        this.convertNodesToTokensForSupportedNodeTypes(["typography"], nodes, brand);
        // Border tokens
        this.convertNodesToTokensForSupportedNodeTypes(["border"], nodes, brand);
        // Fix nodes so they are aligned with the way Supernova expects root groups to be named
        const processedNodes = this.referenceResolver.unmappedValues();
        // aliased tokens are resolved after non-aliased tokens, so restoring initial order
        this.restoreInitialTokenOrder(nodes, processedNodes);
        this.remapRootNodeKeys(processedNodes);
        // Retrieve all tokens
        return processedNodes;
    }
    restoreInitialTokenOrder(nodes, processedNodes) {
        const uniqueGroups = new Set();
        // store initial sort index by token/group path key
        const originalSortOrder = new Map(nodes.reduce((acc, node, index) => {
            const sortIndex = index + 1;
            acc.push([[...node.path, node.name].join("/"), sortIndex]);
            // add fallback group sort key if token name was changed
            const groupSortKey = node.path.join("/");
            if (!uniqueGroups.has(groupSortKey)) {
                uniqueGroups.add(groupSortKey);
                acc.push([[...node.path].join("/"), sortIndex]);
            }
            return acc;
        }, []));
        const getTokenSortOrder = (token) => {
            const key = [...token.path, token.token.name].join("/");
            return (originalSortOrder.get(key) ||
                originalSortOrder.get(token.path.join("/")) ||
                Number.MAX_SAFE_INTEGER);
        };
        processedNodes.sort((a, b) => getTokenSortOrder(a) - getTokenSortOrder(b));
    }
    remapRootNodeKey(nodePath, tokenType, nodeOriginalType, nodeTokenName) {
        let path = Array.from(nodePath);
        let firstSegment = path.splice(0, 1)[0];
        // Remap remote to proper destination
        // TODO: maybe we should make firstSegment typed?
        const type = tokenType;
        switch (type) {
            case TokenType.color:
                firstSegment = "Color";
                break;
            case TokenType.gradient:
                firstSegment = "Gradient";
                break;
            // TEST: Do we use separate keys for all Dimensions
            case TokenType.radius:
                firstSegment = "Radius";
                break;
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.duration:
            case TokenType.zIndex:
            case TokenType.size:
            case TokenType.dimension:
                // firstSegment = "Dimension"
                firstSegment = "Measure";
                break;
            case TokenType.shadow:
                firstSegment = "Shadow";
                break;
            case TokenType.typography:
                firstSegment = "Typography";
                break;
            // TEST: Should we now use separate segments here or String?
            case TokenType.fontFamily:
            case TokenType.fontWeight:
            case TokenType.string:
                firstSegment = "Generic";
                break;
            case TokenType.border:
                firstSegment = "Border";
                break;
            case TokenType.textCase:
            case TokenType.textDecoration:
            case TokenType.visibility:
                firstSegment = "Options";
                break;
            default:
                throw new UnreachableCaseError(type, `Unsupported type ${firstSegment} in remapping of nodes`);
        }
        let secondSegment;
        switch (nodeOriginalType) {
            case "borderRadius":
                secondSegment = "Border Radius";
                break;
            case "borderWidth":
                secondSegment = "Border Width";
                break;
            case "sizing":
                secondSegment = "Sizing";
                break;
            case "fontSizes":
                secondSegment = "Font Size";
                break;
            case "paragraphSpacing":
                secondSegment = "Paragraph Spacing";
                break;
            case "lineHeights":
                secondSegment = "Line Height";
                break;
            case "letterSpacing":
                secondSegment = "Letter Spacing";
                break;
            case "spacing":
                secondSegment = "Spacing";
                break;
            case "opacity":
                secondSegment = "Opacity";
                break;
            case "dimension":
                secondSegment = "Dimension";
                break;
            case "fontFamilies":
                secondSegment = "Font Family";
                break;
            case "fontWeights":
                secondSegment = "Font Weight";
                break;
            default:
                // Other types than listed should be ignored
                break;
        }
        // Remap original type and add extra group, if needed
        if (secondSegment) {
            path = [firstSegment, secondSegment, ...path];
        }
        else {
            path = [firstSegment, ...path];
        }
        // Rebuild key
        const key = DTTokenMerger.buildKey(path, nodeTokenName);
        return { path, key };
    }
    remapRootNodeKeys(nodes) {
        for (const node of nodes) {
            const { path, key } = this.remapRootNodeKey(node.path, node.token.tokenType, node.originalType, node.token.name);
            node.path = path;
            node.key = key;
        }
        return nodes;
    }
    convertNodesToTokensForSupportedNodeTypes(types, nodes, brand) {
        // Filter out only nodes that we want to be resolving - we can't be resolving everything at once
        const filteredNodes = nodes.filter((n) => types.includes(n.type));
        let unprocessedTokens = new Array();
        let originalNodeIndex = -1;
        // Convert atomic tokens, ie. tokens without references
        for (const node of filteredNodes) {
            originalNodeIndex += 1;
            // TODO:fix-sdk-eslint
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            if (!this.referenceResolver.valueHasReference(node.value)) {
                const token = this.convertAtomicNode(node, brand);
                this.referenceResolver.addAtomicToken(token, originalNodeIndex);
            }
            else {
                unprocessedTokens.push([node, originalNodeIndex]);
            }
        }
        // Now we have all atomic tokens processed, we can start creating references
        // References will be emptying pool until they are all resolved (this can take multiple
        // passes, resolving one depth level with each pass)
        let depth = 0;
        const maximumDepth = 100;
        while (unprocessedTokens.length !== 0) {
            const unprocessedDepthTokens = new Array();
            for (const [node, index] of unprocessedTokens) {
                // NOTE: Self-reference now works with original-index logic in addAtomicNode
                // Keep it commented meanwhile to ensure it works on bigger data set
                // let nodeKey = `{${node.path.slice(1).join('.')}.${node.name}}`
                // // We might want to skip more complex self reference (in formula)
                // if (node.value === nodeKey) {
                //   console.log(`Skip self reference ${node.value}`)
                //   continue
                // }
                const token = this.convertReferencedNode(node, brand);
                if (token) {
                    this.referenceResolver.addAtomicToken(token, index);
                }
                else {
                    unprocessedDepthTokens.push([node, index]);
                }
            }
            if (unprocessedDepthTokens.length === 0) {
                break;
            }
            unprocessedTokens = unprocessedDepthTokens;
            depth += 1;
            if (depth > maximumDepth) {
                throw new Error(`Engine was not able to solve references for the following tokens in a reasonable time: \n\n${unprocessedTokens
                    .map(([t, i]) => DTTokenMerger.buildKey(t.path, t.name))
                    .join("\n")}\n\nThis can be caused by few things:\n\n- Not including all token sets you wanted to reference in the mapping file\n- Reference pointing to the token that doesn't exist\nCircular reference where two tokens reference each other\nUsing (other) token for defining strings, instead of numbers (current limitation)\n\nIf you are sure this is not the case, please report the bug on our Discord (https://community.supernova.io) and we'll help you solve it :)`);
            }
        }
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Atomic nodes
    convertAtomicNode(node, brand) {
        const snType = this.convertDTTypeToSupernovaType(node.type);
        switch (snType) {
            case TokenType.dimension:
            case TokenType.size:
            case TokenType.space:
            case TokenType.opacity:
            case TokenType.fontSize:
            case TokenType.lineHeight:
            case TokenType.letterSpacing:
            case TokenType.paragraphSpacing:
            case TokenType.borderWidth:
            case TokenType.radius:
            case TokenType.duration:
            case TokenType.zIndex:
                // @ts-expect-error TS(2322): Type 'DTProcessedTokenNode<DimensionCategoryToken<... Remove this comment to see the full error message
                return this.convertDimensionAtomicNode(node, brand, snType);
            case TokenType.color:
                return this.convertColorAtomicNode(node, brand);
            case TokenType.shadow:
                return this.convertShadowAtomicNode(node, brand);
            case TokenType.gradient:
                return this.convertGradientAtomicNode(node, brand);
            case TokenType.border:
                return this.convertBorderAtomicNode(node, brand);
            case TokenType.typography:
                return this.convertTypographyAtomicNode(node, brand);
            case TokenType.textCase:
                return this.convertTextCaseAtomicNode(node, brand);
            case TokenType.textDecoration:
                return this.convertTextDecorationAtomicNode(node, brand);
            case TokenType.visibility:
                return this.convertVisibilityAtomicNode(node, brand);
            case TokenType.fontFamily:
            case TokenType.fontWeight:
            case TokenType.string:
                // @ts-expect-error TS(2322): Type 'DTProcessedTokenNode<StringCategoryToken<Any... Remove this comment to see the full error message
                return this.convertStringAtomicNode(node, brand, snType);
            default:
                throw new UnreachableCaseError(snType, "Unsupported sn token type:");
        }
    }
    convertColorAtomicNode(node, brand) {
        const constructedToken = ColorToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, [], []);
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    convertDimensionAtomicNode(node, brand, type) {
        const constructedToken = resolveDimension(type).create(type, this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, [], []);
        // @ts-expect-error TS(2344): Type 'DimensionCategoryToken<AnyDimensionTokenValu... Remove this comment to see the full error message
        const result = {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
        return result;
    }
    convertStringAtomicNode(node, brand, type) {
        const constructedToken = resolveString(type).create(type, this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, [], []);
        // @ts-expect-error TS(2344): Type 'StringCategoryToken<AnyStringTokenValue>' do... Remove this comment to see the full error message
        const result = {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
        return result;
    }
    convertTextCaseAtomicNode(node, brand) {
        const constructedToken = TextCaseToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, [], []);
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    convertTextDecorationAtomicNode(node, brand) {
        const constructedToken = TextDecorationToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, [], []);
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    convertVisibilityAtomicNode(node, brand) {
        const constructedToken = VisibilityToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, [], []);
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    convertShadowAtomicNode(node, brand) {
        const constructedToken = ShadowToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, this.referenceResolver, [], []);
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    convertBorderAtomicNode(node, brand) {
        const constructedToken = BorderToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, this.referenceResolver, [], []);
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    convertGradientAtomicNode(node, brand) {
        const constructedToken = GradientToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, this.referenceResolver, [], []);
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    convertTypographyAtomicNode(node, brand) {
        const constructedToken = TypographyToken.create(this.versionId, brand.id, node.name, 
        // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        node.description, 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        node.value, undefined, this.referenceResolver, [], [], DTTokenMerger.buildReference(node.path, node.name));
        return {
            token: constructedToken,
            originalType: node.type,
            path: node.path,
            key: DTTokenMerger.buildKey(node.path, node.name),
        };
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Referenced nodes
    convertReferencedNode(node, brand) {
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (!this.referenceResolver.isBalancedReference(node.value)) {
            throw new Error(`Invalid reference syntax in token value: ${node.value}`);
        }
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        if (this.referenceResolver.valueIsPureReference(node.value)) {
            // Option 1: Pure reference, in which case we can create the token directly based on the reference
            return this.convertPureReferencedNode(node, brand);
        }
        // Option 2: The value is more complex string. We first need to resolve the references, reduce the issue, then try to build the token again
        return this.convertComplexReferenceNode(node, brand);
    }
    // TODO: Can we do better or need to copy paste?
    convertPureReferencedNode(node, brand) {
        var _a, _b;
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const valueAsReference = node.value;
        const resolvedToken = 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.referenceResolver.lookupReferencedToken(valueAsReference);
        if (resolvedToken) {
            let constructedToken;
            // TS can have gradient stored as 'color' type'. We can detect and parse it as Gradient.
            // TS could have token1 with 'color' type, that we parsed as Color, which reference token2 with 'color' type, that we parsed as Gradient.
            // We need to detect this and build token1 as Gradient, not Color
            if (node.type === "color" &&
                resolvedToken.tokenType === TokenType.gradient) {
                node.type = "gradient";
            }
            const snType = this.convertDTTypeToSupernovaType(node.type);
            switch (snType) {
                case TokenType.color:
                    constructedToken = ColorToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, [], []);
                    break;
                case TokenType.dimension:
                case TokenType.size:
                case TokenType.space:
                case TokenType.opacity:
                case TokenType.fontSize:
                case TokenType.lineHeight:
                case TokenType.letterSpacing:
                case TokenType.paragraphSpacing:
                case TokenType.borderWidth:
                case TokenType.radius:
                case TokenType.duration:
                case TokenType.zIndex:
                    constructedToken = resolveDimension(snType).create(snType, this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, [], []);
                    if (resolvedToken && ((_a = constructedToken.origin) === null || _a === void 0 ? void 0 : _a.referencePersistentId)) {
                        const refNode = 
                        // TODO:fix-sdk-eslint
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        this.referenceResolver.lookupReferencedNode(valueAsReference);
                        const { key } = this.remapRootNodeKey(
                        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                        refNode.path, resolvedToken.tokenType, 
                        // @ts-expect-error TS(2532): Object is possibly 'undefined'.
                        refNode.originalType, resolvedToken.name);
                        console.log(`Inlining reference origin id ${valueAsReference}: ${key}: ${(_b = constructedToken.origin) === null || _b === void 0 ? void 0 : _b.referencePersistentId} into ${constructedToken.id}:${node.name}:${node.type}`);
                        constructedToken.origin.referenceOriginId = key;
                    }
                    break;
                case TokenType.shadow:
                    constructedToken = ShadowToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, this.referenceResolver, [], []);
                    break;
                case TokenType.border:
                    constructedToken = BorderToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, this.referenceResolver, [], []);
                    break;
                case TokenType.gradient:
                    constructedToken = GradientToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, this.referenceResolver, [], []);
                    break;
                case TokenType.typography:
                    constructedToken = TypographyToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, this.referenceResolver, [], [], DTTokenMerger.buildReference(node.path, node.name));
                    break;
                case TokenType.fontWeight:
                case TokenType.fontFamily:
                case TokenType.string:
                    constructedToken = resolveString(snType).create(snType, this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, [], []);
                    break;
                case TokenType.textCase:
                    constructedToken = TextCaseToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, [], []);
                    break;
                case TokenType.textDecoration:
                    constructedToken = TextDecorationToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, [], []);
                    break;
                case TokenType.visibility:
                    constructedToken = VisibilityToken.create(this.versionId, brand.id, node.name, 
                    // @ts-expect-error TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
                    node.description, undefined, resolvedToken, [], []);
                    break;
                default:
                    throw new UnreachableCaseError(snType, "Unsupported token type:");
            }
            return {
                token: constructedToken,
                path: node.path,
                originalType: node.type,
                key: DTTokenMerger.buildKey(node.path, node.name),
            };
        }
        return undefined;
    }
    convertComplexReferenceNode(node, brand) {
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const valueAsReference = node.value;
        const references = 
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        this.referenceResolver.lookupAllReferencedTokens(valueAsReference);
        if (!references) {
            return undefined;
        }
        const resolvedValue = this.referenceResolver.replaceAllReferencedTokens(
        // TODO:fix-sdk-eslint
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        valueAsReference, references);
        // Create temporary node with replaced value that will have no more references
        const resolvedNode = {
            rootKey: node.rootKey,
            name: node.name,
            path: node.path,
            type: node.type,
            value: resolvedValue,
            description: node.description,
        };
        const processedNode = this.convertAtomicNode(resolvedNode, brand);
        return processedNode;
    }
    // --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- --- ---
    // MARK: - Convenience
    convertDTTypeToSupernovaType(typeAsString) {
        const type = typeAsString;
        switch (type) {
            case "color":
                return TokenType.color;
            case "borderRadius":
                return TokenType.radius;
            case "boxShadow":
                return TokenType.shadow;
            case "gradient":
                return TokenType.gradient;
            case "typography":
                return TokenType.typography;
            case "border":
                return TokenType.border;
            case "sizing":
                return TokenType.size;
            case "duration":
                return TokenType.duration;
            case "spacing":
                return TokenType.space;
            case "borderWidth":
                return TokenType.borderWidth;
            case "opacity":
                return TokenType.opacity;
            case "fontSizes":
                return TokenType.fontSize;
            case "paragraphSpacing":
                return TokenType.paragraphSpacing;
            case "letterSpacing":
                return TokenType.letterSpacing;
            case "lineHeights":
                return TokenType.lineHeight;
            case "other":
                return TokenType.dimension; // TEST: Elli has tokens with type other that reference opacity, I'd expect this to be generic, not dimension
            case "dimension":
                return TokenType.dimension;
            case "number":
                return TokenType.dimension;
            case "fontFamilies":
                return TokenType.fontFamily;
            case "fontWeights":
                return TokenType.fontWeight;
            case "text":
                return TokenType.string;
            case "textCase":
                return TokenType.textCase;
            case "textDecoration":
                return TokenType.textDecoration;
            default:
                throw new UnreachableCaseError(type, "Unsupported DT token type:");
        }
    }
}
